import React, { useState } from 'react';
import {
  formatShortWhen,
  formatPlanShort,
  formatWhatsappLink,
  formatInstagramLink,
  formatMailLink,
  getCoachBadge,
  getCoachBadgeLabel,
  getTeamCoachNombre,
  getResultadoLabel,
  getResultadoClass,
  isExHeadTeamCoach,
  RESULTADO_LABEL,
  RESULTADOS_ORDEN,
  formatContactadoDate,
  formatFechaLlamada,
  formatFechaCortaBot,
  formatContactarEl,
  formatCambioResultado,
  COACHES_CIERRE,
  getCoachAgendaNombre,
  getCoachCierreNombre,
  esCierreDeOtroCoach,
} from '../../utils/adminLeadsHelpers';

const segClass = (segmento) => {
  const s = String(segmento || '').toLowerCase();
  if (s.includes('caliente')) return 'caliente';
  if (s.includes('calificado') && !s.includes('no')) return 'calificado';
  return 'no_calificado';
};

const segLabel = (segmento) => {
  const s = String(segmento || '').toLowerCase();
  if (s.includes('caliente')) return 'CALIENTE';
  if (s.includes('no_calificado') || s === 'no_calificado') return 'NO CALIFICADO';
  if (s.includes('calificado')) return 'CALIFICADO';
  return (segmento || '').toUpperCase();
};

// Resumen corto del origen para la card del lead.
// Prioriza utm_source/medium/campaign, cae a referrer y por último "Directo".
const formatOrigenResumen = (t = {}) => {
  const parts = [];
  if (t.utm_source) parts.push(t.utm_source);
  if (t.utm_medium) parts.push(t.utm_medium);
  if (t.utm_campaign) parts.push(t.utm_campaign);
  if (parts.length > 0) return parts.join(' · ');
  if (t.referrer) {
    try {
      return new URL(t.referrer).hostname.replace(/^www\./, '');
    } catch {
      return t.referrer;
    }
  }
  return 'Directo';
};

const hasTracking = (t = {}) =>
  Boolean(
    t.utm_source || t.utm_medium || t.utm_campaign || t.utm_content || t.utm_term ||
    t.referrer || t.landing_page || t.gclid || t.fbclid
  );

// Clase de color del estado del bot. Verde solo cuando ya agendó: es el único
// estado que no pide acción de nadie. Rojo para el que más pide acción: el
// teléfono inválido nunca se revive solo (ver agendaWatcher.js).
const botEstadoClass = (label) => {
  if (label === 'AGENDADO') return 'agendado';
  if (label === 'FRÍO') return 'frio';
  if (label === 'TELÉFONO INVÁLIDO') return 'error';
  return '';
};

const LeadEntry = ({ lead, index = 0, rol, onToggleContactado, onSetResultado, botDegradado, hoy }) => {
  // Agendó por el link de reserva sin pasar por el formulario del funnel
  // (típicamente desde una conversación de Instagram). Su ficha NO tiene
  // respuestas del cuestionario, y eso hay que decirlo: si no, la card se ve
  // igual que la de un lead del funnel al que se le perdieron los datos.
  const esReservaDirecta = String(lead.tipo || '').trim() === 'Reserva Directa';
  const cls = segClass(lead.segmento);
  const wa = formatWhatsappLink(lead.whatsapp);
  const ig = formatInstagramLink(lead.instagram);
  const mail = formatMailLink(lead.email);
  const planShort = formatPlanShort(lead.inversion);
  const coachBadge = getCoachBadge(lead);
  const coachBadgeLabel = getCoachBadgeLabel(lead);
  const teamCoachNombre = getTeamCoachNombre(lead);
  const exHead = isExHeadTeamCoach(lead);
  const isContacted = !!lead.contactado;
  const contactedDate = formatContactadoDate(lead.contactado);
  const tracking = lead.tracking || {};
  const origenResumen = formatOrigenResumen(tracking);
  const showOrigenDetalle = hasTracking(tracking);
  const bot = lead.bot || null;
  // Canal caído: el backend solo lo manda cuando de verdad no se puede llegar
  // por ahí. Los canales que siguen vivos se calculan acá para poder decir a
  // dónde escribirle en vez de solo avisar del problema.
  const contactoFallido = lead.contactoFallido || null;
  const canalesVivos = contactoFallido
    ? [
      wa && !contactoFallido.whatsapp ? 'WhatsApp' : null,
      mail && !contactoFallido.email ? 'correo' : null,
      ig ? 'Instagram' : null,
    ].filter(Boolean)
    : [];
  // Si el formato viene inesperado, formatFechaCortaBot devuelve '': no hay
  // que mostrar la etiqueta ("· último ", "sí, ") colgando sin fecha detrás.
  const ultimoEnvioFmt = bot ? formatFechaCortaBot(bot.ultimoEnvio) : '';
  const resultadoLabel = getResultadoLabel(bot);
  const resultadoClass = getResultadoClass(bot);
  // Con quién cerró de verdad. El badge se muestra siempre que esté marcado,
  // pero se pinta distinto cuando NO es el coach que pidió en el formulario:
  // ese es el caso que hay que ver de lejos.
  const coachCierreNombre = getCoachCierreNombre(bot);
  const cierreDeOtro = esCierreDeOtroCoach(lead, bot);
  // De quién es la llamada cuando el lead no llenó el formulario. Solo para
  // reserva directa: en un lead del funnel el coach ya se ve en el badge de
  // arriba, y lo que interesa ahí es con quién QUISO ir, no con quién agendó.
  const coachAgendaNombre = esReservaDirecta ? getCoachAgendaNombre(bot) : null;
  const clicAgendaFmt = bot ? formatFechaCortaBot(bot.clicAgenda) : '';
  const clicCuestionarioFmt = bot ? formatFechaCortaBot(bot.clicCuestionario) : '';

  // Todo lo que le pasó al resultado de este lead, del cambio más viejo al más
  // nuevo. Vacío puede ser "nunca cambió de estado" o "no se pudo leer la
  // pestaña": lo segundo lo dice botDegradado.historial, y el botón de abajo
  // distingue los dos casos en vez de afirmar el primero.
  const historial = bot?.historial || [];

  const [submitting, setSubmitting] = useState(false);
  const [hiloAbierto, setHiloAbierto] = useState(false);
  const [historialAbierto, setHistorialAbierto] = useState(false);
  const [guardandoResultado, setGuardandoResultado] = useState(false);
  const [fechaSeguimiento, setFechaSeguimiento] = useState(bot?.contactarEl || '');
  const [pidiendoFecha, setPidiendoFecha] = useState(false);

  /**
   * Apretar el resultado que ya está puesto lo limpia. Es la forma de corregir
   * una marca equivocada sin gastar un botón extra en "deshacer".
   *
   * El seguimiento es un caso aparte: el backend rechaza un "seguimiento" sin
   * fecha con 400, así que acá no se manda nada todavía — solo se abre el
   * campo de fecha y es `guardarSeguimiento` quien hace el POST.
   */
  const handleResultado = async (r) => {
    if (guardandoResultado) return;
    if (bot.resultado === r) {
      setPidiendoFecha(false);
      // Si no se borra acá, la próxima vez que se abra SEGUIMIENTO el campo
      // aparece precargado con esta fecha vieja y el botón queda habilitado
      // de entrada: quien no note el valor precargado guarda de nuevo una
      // fecha que ya no corresponde.
      setFechaSeguimiento('');
      setGuardandoResultado(true);
      try {
        await onSetResultado(lead, '');
      } catch (err) {
        // onSetResultado ya revirtió y avisó.
      } finally {
        setGuardandoResultado(false);
      }
      return;
    }
    if (r === 'seguimiento') {
      setPidiendoFecha(true);
      return;
    }
    // Se arrepintió de pedir la fecha y eligió otro resultado: el campo se
    // cierra solo, no se queda pegado esperando una fecha que ya no aplica.
    // Misma razón que arriba para limpiar `fechaSeguimiento`.
    setPidiendoFecha(false);
    setFechaSeguimiento('');
    setGuardandoResultado(true);
    try {
      await onSetResultado(lead, r);
    } catch (err) {
      // onSetResultado ya revirtió y avisó.
    } finally {
      setGuardandoResultado(false);
    }
  };

  /**
   * Marca con qué coach cerró el lead que contrató.
   *
   * Apretar el que ya está puesto lo limpia, igual que los botones de
   * resultado: es la forma de deshacer una marca equivocada sin un botón más.
   *
   * Reenvía 'contrato' porque el endpoint pide el resultado en cada llamada.
   * Es el único resultado donde este campo existe, así que no hay nada que
   * preservar: el botón solo se muestra cuando ya está marcado como contrato.
   */
  const handleCerroCon = async (clave) => {
    if (guardandoResultado) return;
    const siguiente = (bot.cerroCon || '') === clave ? '' : clave;
    setGuardandoResultado(true);
    try {
      await onSetResultado(lead, 'contrato', undefined, undefined, siguiente);
    } catch (err) {
      // onSetResultado ya revirtió y avisó.
    } finally {
      setGuardandoResultado(false);
    }
  };

  /** Guarda el seguimiento recién ahora que ya tiene fecha puesta. */
  const guardarSeguimiento = async () => {
    if (!fechaSeguimiento || guardandoResultado) return;
    setPidiendoFecha(false);
    setGuardandoResultado(true);
    try {
      await onSetResultado(lead, 'seguimiento', undefined, fechaSeguimiento);
    } catch (err) {
      // onSetResultado ya revirtió y avisó.
    } finally {
      setGuardandoResultado(false);
    }
  };

  /**
   * La nota se guarda al salir del campo, no en cada tecla.
   *
   * Reenvía la fecha de contacto que ya tenía: el backend rechaza con 400 un
   * `seguimiento` sin fecha, así que sin esto escribir una nota en un lead en
   * seguimiento fallaba en silencio, que es justo el estado donde más se anota.
   */
  const handleNota = async (texto) => {
    if (texto === (bot.nota || '')) return;
    try {
      await onSetResultado(lead, bot.resultado || '', texto, bot.contactarEl || '');
    } catch (err) {
      // onSetResultado ya revirtió y avisó.
    }
  };

  const handleToggle = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onToggleContactado(lead, !isContacted);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className={`au-lead ${cls}${isContacted ? ' contactado' : ''}`}
      style={{ '--au-lead-delay': `${Math.min(index, 24) * 30}ms` }}
    >
      <div className="au-lead-meta">
        <div className="when">{formatShortWhen(lead.fecha)}</div>
        <div className="seg">{esReservaDirecta ? 'RESERVA DIRECTA' : segLabel(lead.segmento)}</div>
        {/* "score --" en un lead que nunca contestó el cuestionario se lee como
            un dato que se perdió. Decir que no hay cuestionario es la verdad. */}
        <div className="score">
          {esReservaDirecta ? 'sin cuestionario' : `score ${lead.score == null ? '--' : lead.score}`}
        </div>
      </div>

      <div className="au-lead-body">
        <div className="name">
          <span>{lead.nombre || '[SIN NOMBRE]'}</span>
          <span className="au-lead-name-extras">
            {esReservaDirecta && (
              <span
                className="au-lead-origen-badge"
                title={`Agendó por el link de reserva sin pasar por el formulario del funnel. No tiene respuestas del cuestionario.${
                  coachAgendaNombre ? ` La llamada quedó agendada con ${coachAgendaNombre}.` : ''
                }`}
              >
                {coachAgendaNombre ? `DM · ${coachAgendaNombre}` : 'DM'}
              </span>
            )}
            {coachBadge && (
              <span
                className={`au-lead-coach-badge ${coachBadge === 'HC' ? 'hc' : 'tc'}${exHead ? ' exhead' : ''}${teamCoachNombre ? ' con-coach' : ''}`}
                title={exHead ? 'Originalmente quiso Head Coach con Agustín · aceptó pasar al equipo AU' : undefined}
              >
                {coachBadgeLabel}
              </span>
            )}
            {resultadoLabel && (
              <span className={`au-lead-resultado-badge ${resultadoClass}`}>
                {resultadoLabel}
              </span>
            )}
            {/* Con quién cerró. Solo aparece marcado a mano: no hay forma de
                deducirlo del pago (ver resultadoLlamadaCalc). */}
            {bot?.resultado === 'contrato' && coachCierreNombre && (
              <span
                className={`au-lead-cierre-badge${cierreDeOtro ? ' otro' : ''}`}
                title={cierreDeOtro
                  ? 'Cerró con un coach distinto del que pidió en el formulario'
                  : 'Cerró con el coach que pidió en el formulario'}
              >
                CERRÓ CON {coachCierreNombre}
              </span>
            )}
            {/* Cuánto quedó pagando. El texto sale tal cual de Flow, que es
                la única fuente confiable: en la cuenta conviven diez planes
                con precios de distintas épocas. */}
            {bot?.resultado === 'contrato' && bot.plan && (
              <span className="au-lead-plan-badge" title="Plan y monto según Flow">
                {bot.plan}
              </span>
            )}
            {bot?.resultado === 'seguimiento' && bot.contactarEl && (
              <span className="au-lead-seguimiento-cuando">
                {formatContactarEl(bot.contactarEl, hoy)}
              </span>
            )}
            {lead.envios > 1 && (
              <span
                className="au-lead-envios-badge"
                title="Esta persona completó el formulario más de una vez. Se muestran los datos del último envío y la fecha del primero; los anteriores están abajo, en la ficha."
              >
                {lead.envios} ENVÍOS
              </span>
            )}
            {isContacted && (
              <span className="au-lead-status-badge">
                CONTACTADO &#10003;{contactedDate ? ` ${contactedDate}` : ''}
              </span>
            )}
          </span>
        </div>

        <div className="au-lead-fields">
          {planShort && (
            <div className="au-lead-field">
              <div className="au-lead-field-label">Plan</div>
              <div className="au-lead-field-value">{planShort}</div>
            </div>
          )}
          {lead.minutoVideo && (
            <div className="au-lead-field">
              <div className="au-lead-field-label">Minuto video</div>
              <div className="au-lead-field-value">{lead.minutoVideo}</div>
            </div>
          )}

          <hr className="au-lead-divider" />

          {lead.experiencia && (
            <div className="au-lead-field">
              <div className="au-lead-field-label">Experiencia</div>
              <div className="au-lead-field-value">{lead.experiencia}</div>
            </div>
          )}
          {lead.situacion && (
            <div className="au-lead-field">
              <div className="au-lead-field-label">Situaci&oacute;n</div>
              <div className="au-lead-field-value">{lead.situacion}</div>
            </div>
          )}
          {lead.urgencia && (
            <div className="au-lead-field">
              <div className="au-lead-field-label">Urgencia</div>
              <div className="au-lead-field-value">{lead.urgencia}</div>
            </div>
          )}
          {lead.frenoActual && (
            <div className="au-lead-field">
              <div className="au-lead-field-label">Freno</div>
              <div className="au-lead-field-value freno-quote">&ldquo;{lead.frenoActual}&rdquo;</div>
            </div>
          )}

          <hr className="au-lead-divider" />

          <div className="au-lead-field">
            <div className="au-lead-field-label">Origen</div>
            <div className="au-lead-field-value">{origenResumen}</div>
          </div>
          {/* Cuando el lead viene de un correo, el UTM solo trae la clave
              ("015-idea-de-fondo"). Acá se muestra de qué correo se trata sin
              tener que ir a buscarlo al panel de email. */}
          {lead.correoDeOrigen && (
            <div className="au-lead-field">
              <div className="au-lead-field-label">Correo</div>
              <div className="au-lead-field-value">
                {lead.correoDeOrigen.etiqueta}
                {lead.correoDeOrigen.asunto && (
                  <div className="muted">&ldquo;{lead.correoDeOrigen.asunto}&rdquo;</div>
                )}
              </div>
            </div>
          )}
          {showOrigenDetalle && (
            <>
              {tracking.utm_content && (
                <div className="au-lead-field">
                  <div className="au-lead-field-label">Anuncio</div>
                  <div className="au-lead-field-value">{tracking.utm_content}</div>
                </div>
              )}
              {tracking.utm_term && (
                <div className="au-lead-field">
                  <div className="au-lead-field-label">Keyword</div>
                  <div className="au-lead-field-value">{tracking.utm_term}</div>
                </div>
              )}
              {tracking.referrer && (
                <div className="au-lead-field">
                  <div className="au-lead-field-label">Referrer</div>
                  <div className="au-lead-field-value">{tracking.referrer}</div>
                </div>
              )}
              {tracking.landing_page && (
                <div className="au-lead-field">
                  <div className="au-lead-field-label">Landing</div>
                  <div className="au-lead-field-value">{tracking.landing_page}</div>
                </div>
              )}
              {(tracking.gclid || tracking.fbclid) && (
                <div className="au-lead-field">
                  <div className="au-lead-field-label">Click ID</div>
                  <div className="au-lead-field-value">
                    {tracking.gclid ? `gclid: ${tracking.gclid}` : ''}
                    {tracking.gclid && tracking.fbclid ? ' · ' : ''}
                    {tracking.fbclid ? `fbclid: ${tracking.fbclid}` : ''}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="au-lead-section" />

        {/* Un canal caído en alguien que postuló. Va arriba de los datos de
            contacto a propósito: es lo primero que hay que saber antes de
            intentar escribirle por ahí. */}
        {contactoFallido && (
          <div className="au-lead-bot-row">
            <div className="au-lead-bot-label">Contacto</div>
            <div className="au-lead-bot-value error">
              {contactoFallido.whatsapp && contactoFallido.email
                ? 'NO LLEGA NI POR WHATSAPP NI POR CORREO'
                : contactoFallido.whatsapp
                  ? 'EL WHATSAPP NO RECIBE'
                  : 'EL CORREO REBOTA'}
              <div className="au-lead-contact-hint">
                {contactoFallido.whatsapp
                  ? `Último intento ${contactoFallido.whatsapp.fecha}: ${contactoFallido.whatsapp.motivo}. `
                  : ''}
                {contactoFallido.email ? `Correo: ${contactoFallido.email}. ` : ''}
                {canalesVivos.length > 0
                  ? `Escríbele por ${canalesVivos.join(' o ')}.`
                  : 'No queda ningún canal registrado.'}
              </div>
            </div>
          </div>
        )}

        <div className="au-lead-contact">
          <div className="au-lead-contact-label">WhatsApp</div>
          <div className={`au-lead-contact-value ${wa ? '' : 'empty'}`}>
            {wa ? <a href={wa} target="_blank" rel="noreferrer">{lead.whatsapp}</a> : '(sin dato)'}
          </div>
          {wa
            ? <a className="au-lead-contact-action" href={wa} target="_blank" rel="noreferrer">&gt;&gt; abrir</a>
            : <span className="au-lead-contact-action disabled">&gt;&gt; abrir</span>}
        </div>

        <div className="au-lead-contact">
          <div className="au-lead-contact-label">Email</div>
          <div className={`au-lead-contact-value ${mail ? '' : 'empty'}`}>
            {mail ? <a href={mail}>{lead.email}</a> : '(sin dato)'}
          </div>
          {mail
            ? <a className="au-lead-contact-action" href={mail}>&gt;&gt; enviar mail</a>
            : <span className="au-lead-contact-action disabled">&gt;&gt; enviar mail</span>}
        </div>

        <div className="au-lead-contact">
          <div className="au-lead-contact-label">Instagram</div>
          <div className={`au-lead-contact-value ${ig ? '' : 'empty'}`}>
            {ig ? <a href={ig} target="_blank" rel="noreferrer">{lead.instagram}</a> : '(sin dato)'}
          </div>
          {ig
            ? <a className="au-lead-contact-action" href={ig} target="_blank" rel="noreferrer">&gt;&gt; abrir IG</a>
            : <span className="au-lead-contact-action disabled">&gt;&gt; abrir IG</span>}
        </div>

        {!bot && lead.botAusente && (
          <>
            <div className="au-lead-bot-section" />
            <div className="au-lead-bot-row">
              <div className="au-lead-bot-label">Estado</div>
              <div className="au-lead-bot-value error">
                SIN REGISTRO EN EL BOT
              </div>
            </div>
          </>
        )}

        {bot && (
          <>
            <div className="au-lead-bot-section" />

            <div className="au-lead-bot-row">
              <div className="au-lead-bot-label">Estado</div>
              <div className={`au-lead-bot-value ${botEstadoClass(bot.estadoLabel)}`}>
                {bot.estadoLabel}
              </div>
            </div>

            {bot.fechaLlamada && (
              <div className="au-lead-bot-row">
                <div className="au-lead-bot-label">Llamada</div>
                <div className="au-lead-bot-value">
                  {formatFechaLlamada(bot.fechaLlamada)}
                  {bot.linkLlamada && (
                    <>
                      {' · '}
                      {bot.linkLlamada.startsWith('https://')
                        ? (
                          <a href={bot.linkLlamada} target="_blank" rel="noreferrer">&gt;&gt; abrir link</a>
                        )
                        : bot.linkLlamada}
                    </>
                  )}
                </div>
              </div>
            )}

            <div className="au-lead-bot-row">
              <div className="au-lead-bot-label">Mensajes</div>
              <div className="au-lead-bot-value">
                {bot.flujosEnviados.length > 0 ? bot.flujosEnviados.join(' ') : 'ninguno todavía'}
                {ultimoEnvioFmt && ` · último ${ultimoEnvioFmt}`}
              </div>
            </div>

            <div className="au-lead-bot-row">
              <div className="au-lead-bot-label">Clic agenda</div>
              <div className="au-lead-bot-value">
                {bot.clicAgenda ? (clicAgendaFmt ? `sí, ${clicAgendaFmt}` : 'sí') : 'no'}
              </div>
            </div>

            <div className="au-lead-bot-row">
              <div className="au-lead-bot-label">Cuestionario</div>
              <div className="au-lead-bot-value">
                {bot.cuestionario
                  ? `respondido, ${bot.cuestionario}`
                  : bot.clicCuestionario
                    ? `abrió el link${clicCuestionarioFmt ? ` el ${clicCuestionarioFmt}` : ''}, sin responder`
                    : 'no'}
              </div>
            </div>

            {bot.derivadoEn && (
              <div className="au-lead-bot-row">
                <div className="au-lead-bot-label">Respondió</div>
                <div className="au-lead-bot-value">
                  sí · derivado a tu WhatsApp
                </div>
              </div>
            )}

            {bot.error && (
              <div className="au-lead-bot-row">
                <div className="au-lead-bot-label">Error</div>
                <div className="au-lead-bot-value error">{bot.error}</div>
              </div>
            )}

            <button
              type="button"
              className="au-lead-bot-toggle"
              onClick={() => setHiloAbierto((v) => !v)}
              disabled={bot.mensajes.length === 0}
            >
              {bot.mensajes.length === 0
                ? (botDegradado?.mensajes
                  ? '>> no se pudo leer la conversación'
                  : '>> sin conversación registrada')
                : `>> ${hiloAbierto ? 'ocultar' : 'ver'} conversación (${bot.mensajes.length})`}
            </button>

            {/* Doble condición a propósito: `hiloAbierto` sobrevive a un "Recargar"
                porque la key del LeadEntry en AdminLeadsViewer no cambia si el lead
                se queda en la misma posición, así que React no remonta el componente.
                Y `leerMensajesBot` devuelve `null` cuando falla la lectura del Sheet, que
                el controller cae a `[]` (para no romper la carga de la vista) — acá arriba
                se distingue ese caso con `botDegradado.mensajes` para no decir "sin
                conversación registrada" cuando en realidad no se pudo leer. Sin este
                chequeo del length quedaría el botón mostrando "ver conversación" con la
                caja del hilo vacía justo abajo. */}
            {hiloAbierto && bot.mensajes.length > 0 && (
              <div className="au-lead-bot-hilo">
                {bot.mensajes.map((m, i) => (
                  <div
                    key={`${m.fecha}-${i}`}
                    className={`au-lead-bot-msg ${m.direccion === 'entrante' ? 'entrante' : ''}`}
                  >
                    <div className="au-lead-bot-msg-head">
                      {m.direccion === 'entrante' ? '<<' : '>>'} {formatFechaCortaBot(m.fecha) || m.fecha}
                      {m.tipo ? ` · ${m.tipo}` : ''}
                      {m.estado ? ` · ${m.estado}` : ''}
                    </div>
                    <div className="au-lead-bot-msg-texto">{m.texto}</div>
                  </div>
                ))}
              </div>
            )}

            <div className="au-lead-resultado">
              <div className="au-lead-bot-label">Resultado de la llamada</div>
              <div className="au-lead-resultado-botones">
                {RESULTADOS_ORDEN.map((r) => (
                  <button
                    key={r}
                    type="button"
                    className={`au-lead-resultado-btn ${r}${bot.resultado === r ? ' active' : ''}`}
                    disabled={guardandoResultado}
                    onClick={() => handleResultado(r)}
                  >
                    {RESULTADO_LABEL[r]}
                  </button>
                ))}
              </div>
              {(pidiendoFecha || bot.resultado === 'seguimiento') && (
                <div className="au-lead-seguimiento-fecha">
                  <label htmlFor={`contactar-${lead.email}`}>Contactar el</label>
                  <input
                    id={`contactar-${lead.email}`}
                    type="date"
                    value={fechaSeguimiento}
                    onChange={(e) => setFechaSeguimiento(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={guardarSeguimiento}
                    disabled={!fechaSeguimiento || guardandoResultado}
                  >
                    Guardar seguimiento
                  </button>
                </div>
              )}
              {/* Con qué coach cerró. Solo en un contrato: en cualquier otro
                  resultado no hay venta que atribuirle a nadie, y el backend
                  borra el campo al cambiar de resultado. */}
              {bot.resultado === 'contrato' && (
                <div className="au-lead-cierre">
                  <span className="au-lead-cierre-label">Cerró con</span>
                  <div className="au-lead-cierre-botones">
                    {COACHES_CIERRE.map((c) => (
                      <button
                        key={c.clave}
                        type="button"
                        className={`au-lead-cierre-btn${bot.cerroCon === c.clave ? ' active' : ''}`}
                        disabled={guardandoResultado}
                        onClick={() => handleCerroCon(c.clave)}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <input
                className="au-lead-resultado-nota"
                type="text"
                placeholder="Nota (opcional)"
                defaultValue={bot.nota || ''}
                disabled={guardandoResultado}
                onBlur={(e) => handleNota(e.target.value)}
              />

              {/* El recorrido completo del lead: quién cambió qué y cuándo.
                  Existe porque la columna Resultado guarda UN valor y quien
                  escribe pisa al anterior — el pago de Flow incluido. Acá se
                  ve lo que había antes. */}
              <button
                type="button"
                className="au-lead-bot-toggle"
                onClick={() => setHistorialAbierto((v) => !v)}
                disabled={historial.length === 0}
              >
                {historial.length === 0
                  ? (botDegradado?.historial
                    ? '>> no se pudo leer el historial'
                    : '>> sin cambios de resultado registrados')
                  : `>> ${historialAbierto ? 'ocultar' : 'ver'} historial (${historial.length})`}
              </button>

              {historialAbierto && historial.length > 0 && (
                <div className="au-lead-historial">
                  {historial.map((h, i) => (
                    <div className="au-lead-historial-item" key={`${h.fecha}-${i}`}>
                      <div className="au-lead-historial-head">
                        {formatFechaCortaBot(h.fecha) || h.fecha}
                        {h.quien ? ` · ${h.quien}` : ''}
                      </div>
                      <div className="au-lead-historial-cambio">
                        {formatCambioResultado(h)}
                        {h.contactarEl ? ` · contactar el ${h.contactarEl}` : ''}
                      </div>
                      {h.nota && <div className="au-lead-historial-nota">{h.nota}</div>}
                    </div>
                  ))}
                </div>
              )}

              {/* Lo que dijo esta persona las veces anteriores que llenó el
                  formulario. Arriba se muestran los datos del último envío,
                  que es el que corrigió errores, pero el anterior puede
                  guardar algo que el nuevo perdió: José Hernández dijo en el
                  primero que quería a Agustín y aceptaba equipo, y en el
                  segundo ya no. */}
              {(lead.enviosAnteriores || []).length > 0 && (
                <div className="au-lead-envios-previos">
                  <div className="au-lead-envios-previos-titulo">
                    {lead.envios} envíos del formulario
                  </div>
                  {lead.enviosAnteriores.map((prev, i) => (
                    <div className="au-lead-envios-previos-item" key={`${prev.fechaIso || i}`}>
                      <div className="au-lead-envios-previos-head">
                        {formatShortWhen(prev.fecha)}
                        {prev.cambios.length === 0 ? ' · sin diferencias' : ''}
                      </div>
                      {prev.cambios.map((c) => (
                        <div className="au-lead-envios-previos-cambio" key={c.campo}>
                          {c.campo}: {String(c.valor)}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {rol === 'admin' && (
          <button
            type="button"
            className={`au-lead-mark ${isContacted ? 'contacted' : ''}`}
            onClick={handleToggle}
            disabled={submitting}
          >
            {submitting ? 'Guardando…' : isContacted ? 'Desmarcar contactado' : 'Marcar contactado'}
          </button>
        )}
      </div>
    </div>
  );
};

export default LeadEntry;
